import React, { useCallback, useEffect, useRef, useState } from 'react';

import { CustomOfferFormValues } from '@/typings/formValues';
import { Control, Controller, UseFormSetValue } from 'react-hook-form';

import { Checkbox, FormControlLabel } from '@mui/material';

import { useGetBusiness } from '~/hooks/apis/business-traveller/useBusinessDetails';
import { useGetEmployee } from '~/hooks/apis/business-traveller/useGetEmployee';

import { customOfferMerchantFeeFieldLabel } from './customOfferMerchantFeeFieldLabel';
import MerchantFeeOverrideConfirmDialog from './dialogs/MerchantFeeOverrideConfirmDialog';

type Props = {
  userId: string;
  offerId?: string;
  control: Control<CustomOfferFormValues>;
  setValue: UseFormSetValue<CustomOfferFormValues>;
  hasOrders?: boolean;
  isSubmitting: boolean;
};

export default function CustomOfferBusinessTravellerMerchantFeeField({
  userId,
  offerId,
  control,
  setValue,
  hasOrders,
  isSubmitting,
}: Props) {
  const [merchantFeeConfirmOpen, setMerchantFeeConfirmOpen] = useState(false);
  const [pendingMerchantFeeValue, setPendingMerchantFeeValue] = useState<boolean | null>(null);

  const { data: employee } = useGetEmployee(userId);

  const businessId = employee?.businessId ?? '';
  const { data: business } = useGetBusiness(businessId);

  const businessMerchantFeeSyncedRef = useRef(false);

  useEffect(() => {
    if (!business || offerId || businessMerchantFeeSyncedRef.current) {
      return;
    }
    setValue('payment_metadata.merchant_fee_enabled', business.enableMerchantFees ?? false);
    businessMerchantFeeSyncedRef.current = true;
  }, [business, offerId, setValue]);

  useEffect(() => {
    businessMerchantFeeSyncedRef.current = false;
    setMerchantFeeConfirmOpen(false);
    setPendingMerchantFeeValue(null);
  }, [userId]);

  const handleMerchantFeeEnabledChange = useCallback(
    (newChecked: boolean, fieldOnChange: (value: boolean) => void) => {
      if (!business) {
        fieldOnChange(newChecked);
        return;
      }
      const businessDefault = business.enableMerchantFees ?? false;
      if (newChecked === businessDefault) {
        fieldOnChange(newChecked);
        return;
      }
      setPendingMerchantFeeValue(newChecked);
      setMerchantFeeConfirmOpen(true);
    },
    [business],
  );

  const confirmMerchantFeeOverride = useCallback(() => {
    if (pendingMerchantFeeValue != null) {
      setValue('payment_metadata.merchant_fee_enabled', pendingMerchantFeeValue);
    }
    setMerchantFeeConfirmOpen(false);
    setPendingMerchantFeeValue(null);
  }, [pendingMerchantFeeValue, setValue]);

  const cancelMerchantFeeOverride = useCallback(() => {
    setMerchantFeeConfirmOpen(false);
    setPendingMerchantFeeValue(null);
  }, []);

  return (
    <>
      <Controller
        name="payment_metadata.merchant_fee_enabled"
        control={control}
        render={({ field }) => (
          <FormControlLabel
            control={
              <Checkbox
                checked={field.value ?? true}
                onBlur={field.onBlur}
                name={field.name}
                inputRef={field.ref}
                disabled={isSubmitting}
                onChange={(e) => handleMerchantFeeEnabledChange(e.target.checked, field.onChange)}
                data-testid="merchant-fee-enabled"
              />
            }
            label={customOfferMerchantFeeFieldLabel}
          />
        )}
      />
      <MerchantFeeOverrideConfirmDialog
        isOpen={merchantFeeConfirmOpen}
        businessName={business?.name ?? ''}
        feesEnabledOnBusiness={business?.enableMerchantFees ?? false}
        pendingMerchantFeeValue={pendingMerchantFeeValue}
        handleClose={cancelMerchantFeeOverride}
        handleConfirm={confirmMerchantFeeOverride}
      />
    </>
  );
}
